import { useState, useRef, useEffect } from 'react';
import { Menu, Settings, LogOut, ChevronDown, User, Shield, GraduationCap, ArrowLeftRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import NotificationBell from '../NotificationBell';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { auth } from '../../firebase';
import { signOut } from 'firebase/auth';

export default function DashboardTopBar({ setSidebarOpen }) {
  const { userData, isAdmin, isInstructor } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getGreetingText = () => {
    if (isAdmin) return 'لوحة الإدارة';
    if (isInstructor) return 'لوحة تحكم المدرب';
    return 'لوحة الطالب';
  };

  const roleConfigs = (() => {
    if (isAdmin) return { text: 'مدير النظام', color: 'bg-rose-500', icon: Shield };
    if (isInstructor) return { text: 'مدرب معتمد', color: 'bg-primary-600', icon: GraduationCap };
    return { text: 'طالب', color: 'bg-emerald-600', icon: User };
  })();

  const RoleIcon = roleConfigs.icon;

  // Determine settings link based on role and current dashboard path
  const isInstructorDashboard = location.pathname.startsWith('/instructor-dashboard');
  const settingsLink = isInstructorDashboard ? '/instructor-dashboard/settings' : '/dashboard/settings';

  return (
    <header className="h-20 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-white/10 flex items-center justify-between px-5 lg:px-8 z-50 sticky top-0 shadow-sm transition-all duration-500" dir="rtl">
      <div className="flex items-center gap-4">
        <button
          className="md:hidden p-2 text-slate-600 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-white/5 rounded-xl transition-colors"
          onClick={() => setSidebarOpen(true)}
        >
          <Menu className="w-6 h-6" />
        </button>
        <h2 className="text-xl md:text-2xl font-black text-slate-900 dark:text-white hidden sm:block">
          {getGreetingText()}
        </h2>
        {/* Mobile brand text */}
        <Link to="/" className="sm:hidden text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-500">
          <img src="/lobab.png" alt="Logo" className="w-10 h-10" />
        </Link>
      </div>

      <div className="flex items-center gap-2 sm:gap-4">
        <NotificationBell />

        {!isAdmin && (
          <Link to="/dashboard/settings?tab=journey" className="flex items-center gap-2 bg-amber-50/50 dark:bg-amber-500/10 backdrop-blur-md border border-amber-200/50 dark:border-amber-500/20 px-3.5 py-1.5 rounded-full shadow-inner mr-1 sm:mr-2 hover:bg-amber-100/60 dark:hover:bg-amber-500/15 hover:shadow-amber-500/20 transition-all group">
            <span className="text-sm font-black text-amber-600 dark:text-amber-400 font-mono tracking-tight" dir="ltr">{userData?.pixels || 0}</span>
            <div className="relative flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 drop-shadow-[0_0_6px_rgba(245,158,11,0.6)] group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                <path d="M11.25 1.5C11.5833 0.833333 12.4167 0.833333 12.75 1.5L15.3582 6.7164C15.6568 7.31351 16.1865 7.84318 16.7836 8.14175L22.5 11.25C23.1667 11.5833 23.1667 12.4167 22.5 12.75L16.7836 15.8582C16.1865 16.1568 15.6568 16.6865 15.3582 17.2836L12.75 22.5C12.4167 23.1667 11.5833 23.1667 11.25 22.5L8.64175 17.2836C8.34318 16.6865 7.81351 16.1568 7.21639 15.8582L1.5 12.75C0.833333 12.4167 0.833333 11.5833 1.5 11.25L7.21639 8.14175C7.81351 7.84318 8.34318 7.31351 8.64175 6.7164L11.25 1.5Z" fill="url(#pixel-glow-topbar)" />
                <path d="M12 5L13.8 8.6C14.1 9.2 14.8 9.9 15.4 10.2L19 12L15.4 13.8C14.8 14.1 14.1 14.8 13.8 15.4L12 19L10.2 15.4C9.9 14.8 9.2 14.1 8.6 13.8L5 12L8.6 10.2C9.2 9.9 9.9 9.2 10.2 8.6L12 5Z" fill="white" opacity="0.7" />
                <defs>
                  <linearGradient id="pixel-glow-topbar" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#FCD34D" />
                    <stop offset="0.5" stopColor="#F59E0B" />
                    <stop offset="1" stopColor="#D97706" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <span className="text-[11px] font-bold text-amber-500/80 dark:text-amber-400/80 hidden sm:inline -mr-1">بيكسلات</span>
          </Link>
        )}

        <div className="h-8 w-[1px] bg-slate-200 dark:bg-white/10 mx-1 hidden sm:block"></div>

        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center gap-3 p-1 sm:pl-4 rounded-2xl hover:bg-slate-100 dark:hover:bg-white/5 transition-all group border border-transparent hover:border-slate-200 dark:hover:border-white/10"
          >
            <div className="relative">
              {/* Decorative Ring based on Role */}
              <div className={`absolute -inset-1 rounded-2xl opacity-20 blur-sm transition-all duration-500 group-hover:opacity-40 group-hover:blur-md ${roleConfigs.color}`}></div>

              <div className="relative w-12 h-12 rounded-xl overflow-hidden border-2 border-white dark:border-zinc-800 shadow-xl transition-all duration-500 group-hover:scale-105 group-hover:shadow-primary-500/20">
                {userData?.photoURL ? (
                  <img src={userData.photoURL} alt={userData.nameAr} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-slate-100 dark:bg-zinc-800 flex items-center justify-center">
                    <User className="w-4 h-4 text-slate-400" />
                  </div>
                )}
              </div>

              {/* Premium Role Insignia */}
              <div className={`absolute -bottom-1.5 -right-1.5 w-6 h-6 rounded-lg border-2 border-white dark:border-zinc-900 flex items-center justify-center ${roleConfigs.color} shadow-lg shadow-black/20 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-12`}>
                <RoleIcon className="w-3.5 h-3.5 text-white drop-shadow-sm" />

                {/* Subtle Glow Pulse bit */}
                <div className={`absolute inset-0 rounded-lg animate-ping opacity-20 ${roleConfigs.color}`}></div>
              </div>
            </div>

            <div className="hidden lg:flex flex-col text-right">
              <span className="text-lg font-black text-slate-800 dark:text-white leading-tight">
                {userData?.nameAr || userData?.displayName || 'مستخدم لباب'}
              </span>
              <span className="text-md font-bold text-slate-400 dark:text-zinc-500">
                {roleConfigs.text}
              </span>
            </div>

            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-300 hidden sm:block ${isOpen ? 'rotate-180 text-primary-500' : ''}`} />
          </button>

          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, y: 15, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                className="absolute top-full left-0 mt-3 w-64 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-2xl border border-slate-200 dark:border-white/10 rounded-2xl shadow-2xl p-2 z-50 ring-1 ring-black/5"
              >
                {/* Mobile Identity Info */}
                <div className="lg:hidden px-4 py-3 mb-2 border-b border-slate-100 dark:border-white/5">
                  <p className="text-lg font-black text-slate-900 dark:text-white truncate">
                    {userData?.nameAr || userData?.displayName}
                  </p>
                  <p className="text-md font-bold text-slate-500 mt-0.5">{roleConfigs.text}</p>
                </div>

                <div className="space-y-1">
                  {/* Dashboard Switcher (if applicable) */}
                  {isInstructor && !isInstructorDashboard && (
                    <Link
                      to="/instructor-dashboard"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 dark:text-zinc-400 hover:bg-primary-50 dark:hover:bg-primary-500/10 hover:text-primary-600 dark:hover:text-primary-400 transition-all font-bold text-sm"
                    >
                      <ArrowLeftRight className="w-5 h-5" />
                      <span>لوحة المدرب</span>
                    </Link>
                  )}
                  {isInstructor && isInstructorDashboard && (
                    <Link
                      to="/dashboard"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 dark:text-zinc-400 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 hover:text-emerald-600 dark:hover:text-emerald-400 transition-all font-bold text-sm"
                    >
                      <ArrowLeftRight className="w-5 h-5" />
                      <span>لوحة الطالب</span>
                    </Link>
                  )}

                  <Link
                    to={settingsLink}
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 dark:text-zinc-400 hover:bg-slate-50 dark:hover:bg-white/5 hover:text-primary-600 dark:hover:text-primary-400 transition-all font-bold text-sm"
                  >
                    <Settings className="w-5 h-5" />
                    <span>إعدادات الحساب</span>
                  </Link>

                  <div className="h-px bg-slate-100 dark:bg-white/5 my-2"></div>

                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-all font-black text-sm"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>تسجيل الخروج</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
}
